import React, { useMemo, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import clsx from 'clsx';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import StatusBadge from '../../components/StatusBadge';
import ExpandablePanel, { EXPAND_DURATION_MS } from '../../components/ExpandablePanel';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';
import { downloadUserAccessPdf, useGetUserAccessReport, UserAccessRow } from '../../queries/Audit';

type RiskFilter = 'all' | 'admins' | 'noRoles' | 'lockedOut' | 'unconfirmed' | 'failedLogins';

const RISK_OPTIONS: { value: RiskFilter; label: string }[] = [
    { value: 'all', label: 'All users' },
    { value: 'admins', label: 'Administrators (level6)' },
    { value: 'noRoles', label: 'No roles assigned' },
    { value: 'lockedOut', label: 'Currently locked out' },
    { value: 'unconfirmed', label: 'Email not confirmed' },
    { value: 'failedLogins', label: 'Has failed sign-in attempts' },
];

function csvCell(value: unknown) {
    const text = value === null || value === undefined ? '' : String(value);
    // Always quote: role and permission lists contain commas, and names can
    // contain quotes.
    return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: unknown[][]) {
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
    // Leading byte-order mark so Excel reads the file as UTF-8 and does not
    // mangle accented names.
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([`${bom}${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function today() {
    return new Date().toISOString().slice(0, 10);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function AuditReport(props: RouteComponentProps) {
    const report = useGetUserAccessReport();

    const [search, setSearch] = useState('');
    const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
    const [roleFilter, setRoleFilter] = useState('all');
    // Two pieces of state so the panel can animate shut before it unmounts:
    // `mounted` keeps the row rendered, `open` drives the transition.
    const [mountedUserId, setMountedUserId] = useState<string | undefined>(undefined);
    const [openUserId, setOpenUserId] = useState<string | undefined>(undefined);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;

    // Same gate as the Users screen. The endpoint enforces this server-side
    // too; this only avoids showing a page that would fail to load.
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    const rows = useMemo(() => report.data?.items ?? [], [report.data]);

    const roleOptions = useMemo(() => {
        const names = new Set<string>();
        rows.forEach((row) => row.roles.forEach((role) => names.add(role.roleName)));
        return [
            { value: 'all', label: 'All roles' },
            ...Array.from(names)
                .sort()
                .map((name) => ({ value: name, label: name })),
        ];
    }, [rows]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();

        return rows.filter((row) => {
            if (term) {
                const haystack = [
                    row.userName,
                    row.email,
                    row.firstName,
                    row.lastName,
                    ...row.roles.map((r) => `${r.roleName} ${r.scopeName}`),
                ]
                    .join(' ')
                    .toLowerCase();
                if (!haystack.includes(term)) return false;
            }

            if (roleFilter !== 'all' && !row.roles.some((r) => r.roleName === roleFilter))
                return false;

            switch (riskFilter) {
                case 'admins':
                    return row.isAdmin;
                case 'noRoles':
                    return row.hasNoRoles;
                case 'lockedOut':
                    return row.isCurrentlyLockedOut;
                case 'unconfirmed':
                    return !row.emailConfirmed;
                case 'failedLogins':
                    return row.accessFailedCount > 0;
                default:
                    return true;
            }
        });
    }, [rows, search, riskFilter, roleFilter]);

    const summary = useMemo(
        () => ({
            total: rows.length,
            admins: rows.filter((r) => r.isAdmin).length,
            noRoles: rows.filter((r) => r.hasNoRoles).length,
            lockedOut: rows.filter((r) => r.isCurrentlyLockedOut).length,
            unconfirmed: rows.filter((r) => !r.emailConfirmed).length,
        }),
        [rows]
    );

    /** One row per user - the overview. */
    const exportSummary = () => {
        const header = [
            'Username',
            'First name',
            'Last name',
            'Email',
            'Email confirmed',
            'Administrator',
            'Roles',
            'Role count',
            'Permission count',
            'Locked out',
            'Lockout ends',
            'Failed sign-in attempts',
            'Two-factor enabled',
        ];
        const body = filtered.map((row) => [
            row.userName,
            row.firstName,
            row.lastName,
            row.email,
            row.emailConfirmed ? 'Yes' : 'No',
            row.isAdmin ? 'Yes' : 'No',
            row.roles.map((r) => `${r.roleName} @ ${r.scopeType}: ${r.scopeName}`).join('; '),
            row.roleCount,
            row.permissionCount,
            row.isCurrentlyLockedOut ? 'Yes' : 'No',
            row.lockoutEnd ?? '',
            row.accessFailedCount,
            row.twoFactorEnabled ? 'Yes' : 'No',
        ]);
        downloadCsv(`bic-user-access-summary-${today()}.csv`, [header, ...body]);
    };

    /**
     * One row per user / role / permission. This is the grain an access review
     * actually needs - it answers "who can do X" by filtering a column.
     */
    const exportDetail = () => {
        const header = [
            'Username',
            'Name',
            'Email',
            'Area',
            'Permission',
            'What it allows',
            'Granted by',
        ];
        const body: (string | number | undefined)[][] = [];
        filtered.forEach((row) => {
            const name = [row.firstName, row.lastName].filter(Boolean).join(' ');

            if (row.effectivePermissions.length === 0) {
                body.push([row.userName, name, row.email, '', '(no permissions)', '', '']);
                return;
            }

            row.effectivePermissions.forEach((permission) => {
                body.push([
                    row.userName,
                    name,
                    row.email,
                    permission.category,
                    permission.code,
                    permission.description,
                    permission.grantedBy.join('; '),
                ]);
            });
        });
        downloadCsv(`bic-user-access-detail-${today()}.csv`, [header, ...body]);
    };

    const toggleExpanded = (userId: string) => {
        if (openUserId === userId) {
            // Let it collapse, then unmount once the transition has finished.
            setOpenUserId(undefined);
            window.setTimeout(() => {
                setMountedUserId((current) => (current === userId ? undefined : current));
            }, EXPAND_DURATION_MS);
            return;
        }

        // Mount first, then open on the next frame so there is a 0-height
        // starting point to animate from.
        setMountedUserId(userId);
        window.requestAnimationFrame(() => setOpenUserId(userId));
    };

    const exportPdf = async () => {
        setPdfError('');
        setPdfLoading(true);
        try {
            await downloadUserAccessPdf();
        } catch (error) {
            setPdfError(error instanceof Error ? error.message : 'Could not generate the PDF.');
        } finally {
            setPdfLoading(false);
        }
    };

    const columns = [
        {
            title: 'User',
            key: 'userName',
            render: (row: UserAccessRow) => (
                <div>
                    <div className="font-semibold">
                        {[row.firstName, row.lastName].filter(Boolean).join(' ') || row.userName}
                    </div>
                    <div className="text-xs opacity-70">{row.userName}</div>
                </div>
            ),
        },
        {
            title: 'Email',
            key: 'email',
            render: (row: UserAccessRow) => (
                <div>
                    <div>{row.email}</div>
                    {!row.emailConfirmed && <StatusBadge tone="warning" label="Not confirmed" />}
                </div>
            ),
        },
        {
            title: 'Roles',
            key: 'roles',
            render: (row: UserAccessRow) => {
                if (row.hasNoRoles) return <StatusBadge tone="warning" label="No roles" />;
                return (
                    <div className="flex flex-col gap-1">
                        {row.roles.map((role) => (
                            <div key={`${role.roleName}-${role.scopeId ?? 'global'}`}>
                                <span className="font-medium">{role.roleName}</span>
                                <span className="opacity-70">
                                    {' '}
                                    @ {role.scopeType}: {role.scopeName}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Permissions',
            key: 'permissionCount',
            render: (row: UserAccessRow) => (
                <button
                    type="button"
                    className="underline"
                    disabled={row.permissionCount === 0}
                    onClick={() => toggleExpanded(row.userId)}
                >
                    {row.permissionCount}
                    {(() => {
                        if (row.permissionCount === 0) return '';
                        return openUserId === row.userId ? ' (hide)' : ' (show)';
                    })()}
                </button>
            ),
        },
        {
            title: 'Account',
            key: 'isCurrentlyLockedOut',
            render: (row: UserAccessRow) => (
                <div className="flex flex-col gap-1">
                    {row.isAdmin && <StatusBadge tone="danger" label="Administrator" />}
                    {row.isCurrentlyLockedOut && <StatusBadge tone="danger" label="Locked out" />}
                    {row.accessFailedCount > 0 && (
                        <span className="text-xs">
                            {row.accessFailedCount} failed sign-in attempt
                            {row.accessFailedCount === 1 ? '' : 's'}
                        </span>
                    )}
                    {row.twoFactorEnabled && <StatusBadge tone="success" label="2FA" />}
                </div>
            ),
        },
    ];

    /** Permission detail, rendered directly beneath the row it belongs to. */
    const renderExpanded = (row: UserAccessRow) => {
        if (mountedUserId !== row.userId) return null;

        // Grouped by area so a reviewer can scan "what can they do to orders"
        // rather than reading one long alphabetical list.
        const byCategory = row.effectivePermissions.reduce<
            Record<string, typeof row.effectivePermissions>
        >((acc, permission) => {
            const list = acc[permission.category] || [];
            list.push(permission);
            acc[permission.category] = list;
            return acc;
        }, {});

        return (
            <ExpandablePanel open={openUserId === row.userId}>
                {/*
                  Recessed background plus an orange left rule, so the detail
                  reads as belonging to the row above rather than as more rows
                  of the users table.
                */}
                <div className="bg-background border-l-4 border-orange-500 px-5 py-4">
                    <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-sm font-semibold text-foreground">
                            Effective permissions
                        </span>
                        <span className="text-sm text-foreground-muted">— {row.userName}</span>
                        <span className="text-xs font-semibold text-orange-500">
                            {row.effectivePermissions.length} total
                        </span>
                    </div>

                    {Object.keys(byCategory)
                        .sort()
                        .map((category) => (
                            <div key={category} className="mb-4 last:mb-0">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs font-bold uppercase tracking-wider text-orange-500">
                                        {category}
                                    </span>
                                    <span className="text-xs text-foreground-muted">
                                        {byCategory[category].length}
                                    </span>
                                </div>

                                <div className="overflow-hidden border border-border rounded-lg shadow-sm">
                                    <table className="w-full border-collapse text-sm">
                                        <thead>
                                            <tr className="bg-surface-muted border-b border-border">
                                                <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2 w-1/3">
                                                    Permission
                                                </th>
                                                <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2">
                                                    What it allows
                                                </th>
                                                <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2 w-1/4">
                                                    Granted by
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {byCategory[category].map((permission, index) => (
                                                <tr
                                                    key={permission.code}
                                                    className={clsx(
                                                        'border-t border-border-subtle',
                                                        index % 2 === 1
                                                            ? 'bg-surface-muted'
                                                            : 'bg-surface'
                                                    )}
                                                >
                                                    <td className="px-3 py-1.5 align-top font-medium text-foreground">
                                                        {permission.code}
                                                    </td>
                                                    <td className="px-3 py-1.5 align-top text-foreground-secondary">
                                                        {permission.description}
                                                    </td>
                                                    <td className="px-3 py-1.5 align-top text-foreground-muted">
                                                        {permission.grantedBy.join(', ')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                </div>
            </ExpandablePanel>
        );
    };

    return (
        <div className="p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-xl font-semibold">Audit Overview</h1>
                    <p className="text-sm opacity-70">
                        Current access for every user. Snapshot taken when this page loaded.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button secondary onClick={() => report.reset()}>
                        Refresh
                    </Button>
                    <Button secondary onClick={exportSummary} disabled={filtered.length === 0}>
                        Export summary (CSV)
                    </Button>
                    <Button secondary onClick={exportDetail} disabled={filtered.length === 0}>
                        Export detail (CSV)
                    </Button>
                    <Button onClick={exportPdf} loading={pdfLoading} disabled={pdfLoading}>
                        {pdfLoading ? 'Generating...' : 'Export to PDF'}
                    </Button>
                </div>
            </div>

            {pdfError && <div className="text-danger text-sm">{pdfError}</div>}

            <div className="flex gap-4 flex-wrap text-sm">
                <span>
                    <strong>{summary.total}</strong> users
                </span>
                <span>
                    <strong>{summary.admins}</strong> administrators
                </span>
                <span>
                    <strong>{summary.noRoles}</strong> without roles
                </span>
                <span>
                    <strong>{summary.lockedOut}</strong> locked out
                </span>
                <span>
                    <strong>{summary.unconfirmed}</strong> unconfirmed email
                </span>
            </div>

            <div className="flex gap-3 flex-wrap items-end">
                <Input
                    label="Search"
                    placeholder="Name, username, email, role or scope"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    width="w-72"
                />
                <Select
                    label="Show"
                    options={RISK_OPTIONS}
                    selected={riskFilter}
                    onChange={(event) => setRiskFilter(event.target.value as RiskFilter)}
                    width="w-60"
                />
                <Select
                    label="Role"
                    options={roleOptions}
                    selected={roleFilter}
                    onChange={(event) => setRoleFilter(event.target.value)}
                    width="w-52"
                />
                <span className="text-sm opacity-70 pb-2">
                    Showing {filtered.length} of {rows.length}
                </span>
            </div>

            <Table<UserAccessRow>
                data={filtered}
                columns={columns}
                loading={report.loading && 'Loading user access...'}
                error={report.error && 'Could not load the access report.'}
                renderExpanded={renderExpanded}
                // Tint the row whose detail is open, so the pair reads as one unit.
                getStatus={(row) => (openUserId === row.userId ? 'bg-accent-soft border-b-0' : '')}
            />
        </div>
    );
}
